<template>
    <modal name="contact-form-modal"
        :adaptive="true"
        :focusTrap="true"
        :width="600"
        height="auto"
        :styles="{ boxShadow: 'none', background: 'transparent' }"
        @before-open="beforeOpen"
        @before-close="beforeClose"
    >
        <div class="rounded-lg bg-white p-8">
            <div class="flex items-center justify-between mb-6">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ is_editing ? $t('Edit Contact') : $t('Add New Contact') }}
                </h2>
                <button
                    type="button"
                    class="text-gray-400 hover:text-gray-600 text-2xl"
                    @click="closeModal"
                >
                    <i class="far fa-times-circle"></i>
                </button>
            </div>

            <form @submit.prevent="submitForm" id="contact-form">
                <div class="space-y-4">
                    <div>
                        <input-text
                            name="name"
                            v-model="form.name"
                            label="Name"
                            placeholder="Name"
                            :required="true"
                        />
                    </div>

                    <div>
                        <input-text
                            name="company"
                            v-model="form.company"
                            label="Company"
                            placeholder="Company"
                        />
                    </div>

                    <div class="flex flex-col sm:flex-row gap-4">
                        <div class="flex-1">
                            <input-text
                                name="email"
                                v-model="form.email"
                                label="Email"
                                placeholder="Email"
                                type="email"
                            />
                        </div>

                        <div class="flex-1">
                            <input-text
                                name="phone"
                                v-model="form.phone"
                                label="Phone"
                                placeholder="Phone"
                            />
                        </div>
                    </div>

                    <div>
                        <input-text
                            name="address_1"
                            v-model="form.address_1"
                            label="Address"
                            placeholder="Address"
                        />
                    </div>

                    <div>
                        <input-text
                            name="address_2"
                            v-model="form.address_2"
                            label="Address 2"
                            placeholder="Address 2"
                        />
                    </div>

                    <div class="flex flex-col sm:flex-row gap-4">
                        <div class="flex-1">
                            <input-text
                                name="city"
                                v-model="form.city"
                                label="City"
                                placeholder="City"
                            />
                        </div>

                        <div class="flex-1">
                            <input-select-states
                                name="state"
                                v-model="form.state"
                                no_selection="Select State"
                            />
                        </div>

                        <div class="flex-1">
                            <input-text
                                name="zip"
                                v-model="form.zip"
                                label="Zip"
                                placeholder="Zip"
                            />
                        </div>
                    </div>

                    <div>
                        <input-label>Contact Types</input-label>
                        <div class="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                            <label
                                v-for="type in available_types"
                                :key="type.value"
                                class="flex items-center space-x-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    :value="type.value"
                                    v-model="form.types"
                                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span class="text-sm text-gray-700">{{ $t(type.label) }}</span>
                            </label>
                        </div>
                    </div>

                    <div v-if="error" class="text-red-600 font-semibold text-sm">
                        {{ error }}
                    </div>

                    <div v-if="errors && errors.length" class="text-red-600 text-sm">
                        <ul class="list-disc list-inside">
                            <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                        </ul>
                    </div>
                </div>

                <div class="flex items-center justify-between pt-6 mt-6 border-t">
                    <button
                        v-if="is_editing"
                        type="button"
                        class="flex items-center text-sm text-red-600 hover:text-red-700 leading-none"
                        @click="deleteContact"
                    >
                        <i class="far fa-trash-alt mr-2"></i>
                        {{ $t('Delete Contact') }}
                    </button>
                    <div v-else></div>

                    <div class="flex items-center space-x-4">
                        <button-gray-outline type="button" @click="closeModal">
                            {{ $t('Cancel') }}
                        </button-gray-outline>

                        <button-blue type="submit" :disabled="submitting">
                            {{ submitting ? $t('Saving...') : $t('Save') }}
                        </button-blue>
                    </div>
                </div>
            </form>
        </div>
    </modal>
</template>

<script>
import ButtonBlue from '../../components/button-blue.vue';
import ButtonGrayOutline from '../../components/button-gray-outline.vue';
import InputText from '../../components/input-text.vue';
import InputLabel from '../../components/input-label.vue';
import InputSelectStates from '../../components/input-select-states.vue';

export default {
    components: {
        ButtonBlue,
        ButtonGrayOutline,
        InputText,
        InputLabel,
        InputSelectStates,
    },

    props: {
        contact: { default: null },
    },

    data() {
        return {
            is_editing: false,
            contact_id: null,
            submitting: false,
            error: null,
            errors: [],
            form: {
                name: '',
                company: '',
                email: '',
                phone: '',
                address_1: '',
                address_2: '',
                city: '',
                state: '',
                zip: '',
                types: [],
            },
            available_types: [
                { value: 'closing_agent', label: 'Closing Agent' },
                { value: 'home_office_coordinator', label: 'Home Office Coordinator' },
                { value: 'managing_broker', label: 'Managing Broker' },
                { value: 'office_manager', label: 'Office Manager' },
                { value: 'transaction_coordinator', label: 'Transaction Coordinator' },
                { value: 'seller_broker', label: 'Seller Broker' },
                { value: 'buyer_broker', label: 'Buyer Broker' },
                { value: 'inspector', label: 'Inspector' },
                { value: 'buyer_attorney', label: 'Buyer Attorney' },
                { value: 'seller_attorney', label: 'Seller Attorney' },
                { value: 'buyer_mortgage_broker', label: 'Buyer Mortgage Broker' },
                { value: 'buyer_real_estate_agent', label: 'Buyer Real Estate Agent' },
                { value: 'broker_contact', label: 'Broker Contact' },
                { value: 'buyer_contact', label: 'Buyer Contact' },
            ],
        }
    },

    methods: {
        beforeOpen(event) {
            if (event.params && event.params.contact) {
                // Editing existing contact
                this.is_editing = true;
                const contact = event.params.contact;
                this.form = {
                    name: contact.name || '',
                    company: contact.company || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    address_1: contact.address_1 || '',
                    address_2: contact.address_2 || '',
                    city: contact.city || '',
                    state: contact.state || '',
                    zip: contact.zip || '',
                    types: contact.types || [],
                };
                this.contact_id = contact.contact_id || contact.id;
            } else {
                // Adding new contact
                this.is_editing = false;
                this.resetForm();
            }
            this.error = null;
            this.errors = [];
        },

        beforeClose() {
            this.resetForm();
            this.$emit('closed');
        },

        closeModal() {
            this.$modal.hide('contact-form-modal');
        },

        resetForm() {
            this.form = {
                name: '',
                company: '',
                email: '',
                phone: '',
                address_1: '',
                address_2: '',
                city: '',
                state: '',
                zip: '',
                types: [],
            };
            this.contact_id = null;
            this.error = null;
            this.errors = [];
        },

        submitForm() {
            if (!this.form.name) {
                this.error = this.$t('Name is required.');
                return;
            }

            this.submitting = true;
            this.error = null;
            this.errors = [];

            const url = this.is_editing
                ? `/broker/contacts/${this.contact_id}`
                : '/broker/contacts';

            const method = this.is_editing ? 'patch' : 'post';

            axios[method](url, this.form)
                .then(response => {
                    this.submitting = false;
                    this.$emit('saved', response.data.contact);
                    this.$modal.hide('contact-form-modal');
                })
                .catch(error => {
                    this.submitting = false;
                    if (error.response && error.response.data) {
                        if (error.response.data.errors) {
                            this.errors = Object.values(error.response.data.errors).flat();
                        } else if (error.response.data.message) {
                            this.error = error.response.data.message;
                        }
                    } else {
                        this.error = this.$t('An error occurred. Please try again.');
                    }
                });
        },

        deleteContact() {
            if (!confirm(this.$t('Are you sure you want to delete this contact?'))) {
                return;
            }

            axios.delete(`/broker/contacts/${this.contact_id}`)
                .then(response => {
                    this.$emit('deleted', this.contact_id);
                    this.$modal.hide('contact-form-modal');
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.error = error.response.data.message || this.$t('Failed to delete contact.');
                    } else {
                        this.error = this.$t('An error occurred. Please try again.');
                    }
                });
        },
    },
}
</script>

<style scoped>
.vm--overlay {
    background: #000000;
    opacity: 0.7;
}
</style>

